"use client";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useListingAnalytics } from "@/hooks/use-listing-analytics";
import { valuesWithAppName } from "@/lib/i18n";
import type { DetailedListing } from "@/lib/types";
import { buildListingUrl } from "@/lib/url";
import { useTranslations } from "next-intl";

export function ShareListingButton({ listing }: { listing: DetailedListing }) {
  const t = useTranslations();
  const { user } = useCurrentUser();
  const { trackShare } = useListingAnalytics();

  const shortAddress = listing?.business?.address
    ? `${listing.business.address.city}, ${listing.business.address.state}`
    : "N/A";

  const title =
    listing.pitch ??
    t(
      "saas.listing.header.title.masked",
      valuesWithAppName(t, {
        name: listing.pitch ?? "N/A",
        category: listing.businessType?.name ?? "N/A",
        address: shortAddress,
      }),
    );

  async function onShareClick() {
    try {
      await trackShare();
    } catch (err) {
      console.error(err);
      // it is what it is
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: listing.description ?? "",
          url: buildListingUrl(listing),
        });
      } catch (err) {
        console.error(err);
      }
    } else {
      // Handle special snowflake browsers, such as Firefox
      try {
        await navigator.clipboard.writeText(buildListingUrl(listing));
        alert("Link copied to clipboard!");
      } catch (err) {
        console.error(err);
      }
    }
  }

  return (
    <Button variant="ghost" onClick={onShareClick}>
      <Icon icon="Share1" />
      <span className="hidden md:block">
        {t("saas.listing.header.actions.share")}
      </span>
    </Button>
  );
}

ShareListingButton.displayName = "ShareListingButton";
